// The address and og:locale a marketing page gives for itself. A translated
// page (/de/pricing/) is its own page, as the build writes it
// (scripts/lib/translated-pages.mjs): canonical and og:url
// https://www.offlico.com/de/pricing/ (the address its hreflang alternates list
// for German) and og:locale de_DE. English pages keep their address and en_GB.
import { canonicalProductUrl } from "../../utils/siteDomains.js";
import { getLanguage } from "../countries.js";
import { isLocalizedPath, splitLanguagePrefix } from "./routing.js";

export const ENGLISH_OG_LOCALE = "en_GB";
// Translated pages exist on www only (the build writes them there).
const SITE_HOST = "www.offlico.com";

// "de" -> "de", "no" -> "nb": the code of <html lang> and inLanguage.
function htmlLangFor(code) {
  return code === "no" ? "nb" : code;
}

// "de" -> "de_DE", "no" -> "nb_NO": the language with its own country.
export function ogLocaleForLanguage(code) {
  const language = getLanguage(code);
  if (!language || language.code === "en") return ENGLISH_OG_LOCALE;
  return htmlLangFor(language.code) + "_" + (language.defaultCountry || "GB");
}

// englishUrl: the canonical address of the English page; pathname: the
// address being shown. { canonicalUrl, ogLocale } for the page in its language.
export function pageSeoAddress(englishUrl, pathname = typeof window === "undefined" ? "/" : window.location.pathname) {
  const english = { canonicalUrl: englishUrl, ogLocale: ENGLISH_OG_LOCALE };
  const { language, prefix } = splitLanguagePrefix(pathname);
  if (!language || !prefix) return english;
  let url;
  try {
    url = new URL(englishUrl);
  } catch {
    return english;
  }
  // Callers that pass the address shown give it with the prefix already.
  const englishPath = splitLanguagePrefix(url.pathname).path;
  if (!isLocalizedPath(englishPath)) return english;
  url.pathname = prefix + englishPath;
  return { canonicalUrl: url.href, ogLocale: ogLocaleForLanguage(language) };
}

// Text a person reads in structured data (the keys the build translated before).
const JSON_LD_TEXT_KEYS = new Set(["name", "description", "headline", "text", "caption", "alternateName", "slogan"]);
// Addresses of pages. "@id" stays: it names a thing (the app, the company), not a page.
const JSON_LD_PAGE_KEYS = new Set(["url", "item", "mainEntityOfPage"]);
// Items that are pages or writing (schema.org CreativeWork) carry inLanguage.
const JSON_LD_PAGE_TYPES = new Set([
  "WebPage", "CollectionPage", "FAQPage", "AboutPage", "ContactPage", "ItemPage", "QAPage", "Article", "BlogPosting", "HowTo",
]);
// Only the English pages say where the business serves (the United Kingdom):
// a translated page is for readers in other countries (1 Nov 2026).
const JSON_LD_ENGLISH_ONLY_KEYS = new Set(["areaServed"]);

// Structured data of the page being shown. On a translated page (/de/tools/):
// text in the page language (translateLine), www pages at the language's own
// address when they exist in it (/de/tools/), inLanguage on page items, and no
// areaServed. English pages get the payload back as it is. The build passes
// the page's language and prefix (draft languages it previews aren't live).
export function localizeJsonLd(payload, { pathname = typeof window === "undefined" ? "/" : window.location.pathname, language: pageLanguage, prefix: pagePrefix, translateLine = (text) => text } = {}) {
  const { language, prefix } = pageLanguage ? { language: pageLanguage, prefix: pagePrefix } : splitLanguagePrefix(pathname);
  if (!language || language === "en" || !prefix) return payload;
  const inLanguage = htmlLangFor(language);
  const pageAddress = (value) => {
    let url;
    try {
      url = new URL(canonicalProductUrl(value) || value);
    } catch {
      return value;
    }
    const path = splitLanguagePrefix(url.pathname).path;
    if (url.hostname !== SITE_HOST || !isLocalizedPath(path)) return value;
    url.pathname = prefix + path;
    return url.href;
  };
  const visit = (value, key) => {
    if (typeof value === "string") {
      const isUrl = /^https?:\/\//.test(value);
      if (isUrl && JSON_LD_PAGE_KEYS.has(key)) return pageAddress(value);
      return !isUrl && JSON_LD_TEXT_KEYS.has(key) ? translateLine(value) : value;
    }
    if (Array.isArray(value)) return value.map((item) => visit(item, key));
    if (!value || typeof value !== "object") return value;
    const localized = Object.fromEntries(
      Object.entries(value).filter(([name]) => !JSON_LD_ENGLISH_ONLY_KEYS.has(name)).map(([name, item]) => [name, visit(item, name)]),
    );
    if (JSON_LD_PAGE_TYPES.has(localized["@type"]) && !localized.inLanguage) localized.inLanguage = inLanguage;
    return localized;
  };
  return visit(payload, "");
}
